// commission-calculator.js
import React, { useState, useEffect } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "Kalkulator Komisi - Hitung Komisi Penjualan dan Persentase dengan Akurat";
const DESCRIPTION = "Kalkulator komisi gratis - hitung nilai komisi penjualan, total jumlah setelah potongan, dan persentase keuntungan dengan mudah. Alat akurat untuk penjual dan perusahaan untuk menghitung hak finansial secara instan.";
const KEYWORDS = "kalkulator komisi, hitung komisi penjualan, program hitung komisi, cara menghitung komisi saya, commission calculator, hitung persentase penjualan";
const OG_URL = "https://do-calculate.com/calculator/id/commission-calculator/";

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Kalkulator Komisi",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "FinancialApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Beranda",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Kalkulator",
        item: "https://do-calculate.com/id/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Kalkulator Komisi",
        item: OG_URL,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Bagaimana cara menghitung nilai komisi?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Komisi dihitung dengan mengalikan total nilai penjualan dengan persentase komisi kemudian membagi hasilnya dengan 100. Sebagai contoh, jika penjualan 1000 riyal dan persentase 5%, maka komisi sama dengan 50 riyal.",
        },
      },
      {
        "@type": "Question",
        name: "Apa perbedaan antara komisi sederhana dan komisi bertahap?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Komisi sederhana dihitung sebagai persentase tetap dari total penjualan berapa pun nilainya, sedangkan komisi bertahap berubah persentasenya berdasarkan volume penjualan yang dicapai, di mana persentase meningkat seiring dengan peningkatan penjualan untuk memotivasi penjual.",
        },
      },
      {
        "@type": "Question",
        name: "Apakah komisi termasuk pajak pertambahan nilai?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Biasanya, komisi dihitung berdasarkan harga jual bersih sebelum menambahkan pajak pertambahan nilai, kecuali jika kontrak antara kedua belah pihak menentukan lain.",
        },
      },
    ],
  },
];

function CommissionCalculator() {
  const [baseAmount, setBaseAmount] = useState("");
  const [commissionRate, setCommissionRate] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const calculateCommission = () => {
    try {
      setError("");
      const amount = parseFloat(baseAmount);
      const rate = parseFloat(commissionRate);
      const qty = parseInt(quantity) || 1;

      if (isNaN(amount) || isNaN(rate)) {
        setResult(null);
        return;
      }

      if (amount < 0 || rate < 0) {
        setError("Harap masukkan nilai positif");
        setResult(null);
        return;
      }

      const totalSales = amount * qty;
      const commissionAmount = (totalSales * rate) / 100;
      const remainingAmount = totalSales - commissionAmount;

      setResult({
        totalSales,
        commissionAmount,
        remainingAmount,
        rate,
        qty,
      });
    } catch (err) {
      setError("Terjadi kesalahan dalam perhitungan");
      setResult(null);
    }
  };

  useEffect(() => {
    calculateCommission();
  }, [baseAmount, commissionRate, quantity]);

  const handleClear = () => {
    setBaseAmount("");
    setCommissionRate("");
    setQuantity(1);
    setResult(null);
    setError("");
  };

  return (
    <div className="ltr" >
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "5px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>Kalkulator Komisi</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                <strong>Kalkulator Komisi</strong> adalah alat keuangan yang sangat diperlukan bagi setiap pemasar, penjual, atau pemilik bisnis yang ingin <strong>menghitung keuntungan</strong> dan persentase dengan akurat. Baik Anda bekerja di bidang properti, e-commerce, atau penjualan langsung, kalkulator ini membantu Anda mengetahui hak Anda secara instan berdasarkan total penjualan dan persentase komisi yang disepakati.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />
            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>
                  Masukkan Data Penjualan dan Persentase
                </h2>
              </div>

              <div className="row">
                <div className="col-sm-12 col-md-6">
                  <label >Harga Satuan / Jumlah Dasar</label>
                  <input
                    type="number"
                    className="form-control"
                    value={baseAmount}
                    onChange={(e) => setBaseAmount(e.target.value)}
                    placeholder="Contoh: 1000"
                  />
                </div>
                <div className="col-sm-12 col-md-6">
                  <label >Persentase Komisi (%)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={commissionRate}
                    onChange={(e) => setCommissionRate(e.target.value)}
                    placeholder="Contoh: 5"
                  />
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-sm-12 col-md-6">
                  <label >Kuantitas (Jumlah Penjualan)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    min="1"
                  />
                </div>
              </div>

              <div className="text-center mb-3 mt-4">
                <button
                  onClick={handleClear}
                  className="btn btn-secondary"
                  style={{ fontSize: "16px", padding: "10px 30px" }}
                >
                  Hapus Data
                </button>
              </div>

              {error && (
                <div className="alert alert-warning" role="alert">
                  <strong>Peringatan:</strong> {error}
                </div>
              )}

              {result && (
                <div className="mt-4">
                  <h3 style={{ fontSize: "18px", marginBottom: "15px", textAlign: "center" }}>
                    <strong>Hasil Perhitungan:</strong>
                  </h3>
                  <div className="mb-3">
                    <table className="table bmi-table" style={{ width: "100%" }}>
                      <thead>
                        <tr>
                          <th className="bmi-header" style={{ textAlign: "center" }}>Item</th>
                          <th className="bmi-header" style={{ textAlign: "center" }}>Nilai</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ textAlign: "center" }}>Total Penjualan</td>
                          <td style={{ textAlign: "center", fontWeight: "bold" }}>
                            {result.totalSales.toLocaleString()}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>Nilai Komisi yang Diterima</td>
                          <td style={{ textAlign: "center", fontSize: "18px", fontWeight: "bold", color: "#1a5f3f" }}>
                            {result.commissionAmount.toLocaleString()}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>Jumlah Bersih Setelah Komisi</td>
                          <td style={{ textAlign: "center", fontSize: "18px", fontWeight: "bold", color: "#0f3d28" }}>
                            {result.remainingAmount.toLocaleString()}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="info-sections-container">
                    <div className="info-section">
                      <div className="info-section-header">
                        <strong>Detail Rumus</strong>
                      </div>
                      <div className="info-section-body">
                        <p>Komisi = (Total Penjualan × Persentase Komisi) ÷ 100</p>
                        <p>Perhitungan: ({result.totalSales} × {result.rate}%) = {result.commissionAmount}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <hr />
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>

      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                ### Apa itu Kalkulator Komisi dan Bagaimana Cara Menggunakannya?
              </div>
              <div className="info-section-body">
                <p>Kalkulator komisi adalah alat digital yang dirancang untuk membantu individu dan perusahaan menentukan jumlah uang yang berhak diterima agen atau penjual atas penyelesaian transaksi tertentu. Prosesnya didasarkan pada persentase dari total harga jual.</p>
                <p><strong>Langkah Penggunaan:</strong></p>
                <ul>
                  <li>Masukkan harga produk atau layanan yang dijual.</li>
                  <li>Tentukan persentase komisi yang disepakati (misalnya 5% atau 10%).</li>
                  <li>Masukkan jumlah unit yang terjual di kolom kuantitas.</li>
                  <li>Kalkulator akan secara otomatis menampilkan total jumlah komisi dan sisa uang untuk perusahaan.</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                ### Penggunaan Kalkulator Komisi dalam Kehidupan Nyata
              </div>
              <div className="info-section-body">
                <ul>
                  <li><strong>Perantara Properti:</strong> Untuk menghitung bagian broker dari nilai penjualan atau sewa properti.</li>
                  <li><strong>Tenaga Penjual:</strong> Untuk memantau keuntungan bulanan berdasarkan target yang dicapai.</li>
                  <li><strong>E-commerce:</strong> Menghitung biaya yang diambil platform seperti Amazon atau Noon dari pedagang.</li>
                  <li><strong>Pemasaran Afiliasi:</strong> Mengetahui keuntungan yang diharapkan dari promosi tautan dan produk digital.</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                ### Konsep Dasar dalam Sistem Komisi
              </div>
              <div className="info-section-body">
                <p>Sistem komisi berbeda-beda tergantung sifat pekerjaan, beberapa didasarkan pada "komisi bersih" dan lainnya menggunakan sistem "gaji plus komisi".</p>
                <table className="table bmi-table">
                  <thead>
                    <tr>
                      <th>Jenis</th>
                      <th>Deskripsi</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Komisi Tetap</td>
                      <td>Jumlah tertentu untuk setiap transaksi penjualan terlepas dari harganya.</td>
                    </tr>
                    <tr>
                      <td>Persentase</td>
                      <td>Bagian dari total nilai faktur.</td>
                    </tr>
                    <tr>
                      <td>Komisi Bertahap</td>
                      <td>Persentase meningkat seiring dengan peningkatan volume total penjualan.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                ### Contoh Praktis Perhitungan Komisi
              </div>
              <div className="info-section-body">
                <p><strong>Contoh Pertama:</strong> Penjual peralatan rumah tangga menjual mesin cuci seharga 2000 riyal, dengan persentase komisi 3%. Komisi = 2000 × 0.03 = 60 riyal.</p>
                <p><strong>Contoh Kedua:</strong> Pemasar properti menjual apartemen senilai 500.000 riyal dengan persentase komisi 2.5%. Komisi = 500.000 × 0.025 = 12.500 riyal.</p>
                <p><strong>Contoh Ketiga:</strong> Penjualan 10 unit produk dengan harga satuan 100 riyal dengan persentase 10%. Total penjualan 1000, dan komisi 100 riyal.</p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                ### Tips untuk Memastikan Perhitungan Komisi yang Benar
              </div>
              <div className="info-section-body">
                <ul>
                  <li>Pastikan selalu apakah persentase dihitung sebelum atau setelah pajak pertambahan nilai.</li>
                  <li>Simpan catatan akurat untuk semua faktur untuk mencocokkannya dengan hasil kalkulator.</li>
                  <li>Jika ada retur, ingat untuk mengurangkan nilainya dari total penjualan Anda sebelum menghitung komisi akhir.</li>
                  <li>Perhatikan pecahan desimal dalam persentase besar untuk menghindari perbedaan jumlah.</li>
                </ul>
              </div>
            </div>
          </div>

          <br />
          <h2 style={{ fontSize: "20px", marginBottom: "15px" }}>Pertanyaan Umum</h2>
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>Bagaimana cara menghitung komisi saya dari penjualan secara manual?</h3>
              </div>
              <div className="info-section-body">
                Untuk menghitung komisi secara manual, kalikan total nilai penjualan dengan persentase dan bagi dengan 100. Misalnya, jika Anda menjual barang seharga 5000 riyal dengan persentase 2%, kalikan 5000 dengan 2 untuk mendapatkan 10000, lalu bagi dengan 100 sehingga hasilnya 100 riyal. Metode tradisional ini memastikan Anda mengetahui keuntungan dari setiap transaksi dengan cepat.
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>Berapa persentase komisi yang umum dalam properti?</h3>
              </div>
              <div className="info-section-body">
                Di sebagian besar negara Arab, persentase komisi perantara properti berkisar antara 2% hingga 2.5% dari nilai properti. Persentase ini biasanya ditanggung oleh pembeli atau penyewa, tetapi kesepakatan dapat bervariasi tergantung pada kontrak antara para pihak. Penting untuk mendokumentasikan persentase ini dalam kontrak perantara untuk memastikan hak semua pihak.
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>Apakah komisi dihitung berdasarkan harga total atau keuntungan?</h3>
              </div>
              <div className="info-section-body">
                Dalam kebanyakan sistem komersial, komisi dihitung berdasarkan total harga jual akhir faktur. Namun, ada perusahaan yang lebih suka memberikan komisi berdasarkan "margin keuntungan" (selisih antara harga jual dan biaya), jenis ini memotivasi perwakilan untuk menjual dengan harga lebih tinggi untuk meningkatkan keuntungan perusahaan dan keuntungan pribadi mereka.
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>Apa yang dimaksud dengan sistem komisi bertahap?</h3>
              </div>
              <div className="info-section-body">
                Sistem komisi bertahap adalah metode insentif di mana persentase meningkat setiap kali penjual mencapai penjualan lebih banyak. Contoh: Anda mungkin mendapatkan 5% untuk 10.000 riyal pertama penjualan, kemudian persentase meningkat menjadi 7% untuk jumlah yang melebihi itu. Sistem ini bertujuan untuk mendorong karyawan melampaui target penjualan yang ditetapkan untuk mereka.
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>Bagaimana cara menangani pajak pertambahan nilai saat menghitung komisi?</h3>
              </div>
              <div className="info-section-body">
                Aturan umumnya adalah menghitung komisi berdasarkan nilai produk asli sebelum menambahkan pajak. Jika harga produk 100 riyal dan pajak 15 riyal, maka komisi Anda dihitung hanya dari 100 riyal. Menghitung komisi termasuk pajak dapat menyebabkan ketidakakuratan keuangan dalam akuntansi perusahaan karena pajak adalah jumlah yang dikumpulkan untuk negara.
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>Apakah ada batas maksimum komisi secara hukum?</h3>
              </div>
              <div className="info-section-body">
                Hukum berbeda dari satu negara ke negara lain dan dari satu sektor ke sektor lain. Di sektor properti misalnya, beberapa kementerian menetapkan batas atas komisi untuk mencegah eksploitasi. Dalam kontrak kerja dan penjualan swasta, biasanya kembali pada kesepakatan antara kedua belah pihak selama tidak melanggar undang-undang ketenagakerjaan setempat.
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>Apa perbedaan antara komisi dan bonus?</h3>
              </div>
              <div className="info-section-body">
                Komisi adalah jumlah yang terkait langsung dengan setiap transaksi penjualan individu dan dianggap sebagai bagian dasar dari pendapatan. Sedangkan bonus adalah jumlah tambahan yang biasanya diberikan saat mencapai target kolektif atau tahunan, dan tidak dihitung sebagai persentase dari setiap faktur. Komisi adalah bahan bakar harian penjual sementara bonus adalah apresiasi untuk kinerja keseluruhan yang luar biasa.
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>Bagaimana cara menghitung komisi retur?</h3>
              </div>
              <div className="info-section-body">
                Saat pelanggan mengembalikan produk, nilai komisi yang dibayarkan kepada penjual dikurangkan dari haknya berikutnya. Perusahaan biasanya melakukan "rekonsiliasi" bulanan, di mana komisi yang jatuh tempo dikumpulkan dan komisi retur dikurangkan untuk memastikan bahwa penjual menerima komisi hanya untuk transaksi yang sukses dan final.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CommissionCalculator;